import React, { useCallback, useState } from "react";
import { default as ReactSelect } from "react-select";

export const customSelectInputStyles = (theme) => {
    let borderColor;
    let bgColor;
    let color;
    let indicatorBgColor;
    let indicatorBorderColor;
    let indicatorFillColor;
    let shadow;
    let focusBorderColor;
    let hoverBorderColor;
    let placeholderColor;
    switch (theme) {
        case "reverse-contrast":
            borderColor = "#2b2b2b";
            focusBorderColor = "#c2beb9";
            hoverBorderColor = "#c5cad3";
            bgColor = "#1b1b1b";
            color = "#c1c1c1";
            indicatorBgColor = "#1b1b1b";
            indicatorBorderColor = "#2b2b2b";
            indicatorFillColor = "#c1c1c1";
            shadow = "rgba(62, 62, 65, 0.5)";
            placeholderColor = "#c1c1c1"; // Meets 4.5:1 contrast on dark background
            break;
        default:
            borderColor = "#CED4DA";
            focusBorderColor = "#3d4146";
            hoverBorderColor = "#3a352c";
            bgColor = "#ffffff";
            color = "#3d4146";
            indicatorBgColor = "#f9f9fb";
            indicatorBorderColor = "#e4e7eb";
            indicatorFillColor = "#3d4146";
            shadow = "rgba(0, 0, 0, 0.15)";
            placeholderColor = "#3d4146"; // Meets 4.5:1 contrast on white background
            break;
    }
    return {
        control: (styles, state) => ({
            ...styles,
            boxShadow: 0,
            borderColor: state.isFocused ? focusBorderColor : borderColor,
            outline: state.isFocused? '2px solid #1256C1': 'none',
            backgroundColor: bgColor,
            cursor: "pointer",
            "&:hover": {
                borderColor: hoverBorderColor,
            },
        }),
        menu: (styles, state) => ({
            ...styles,
            boxShadow: "0px 2px 8px " + shadow,
        }),
        menuList: (styles) => ({
            ...styles,
            maxHeight: '200px',
            overflowY: 'auto',
        }),
        singleValue: (styles, state) => ({
            ...styles,
            color: color,
        }),
        placeholder: (styles, state) => ({
            ...styles,
            color: placeholderColor, // Fix contrast ratio for placeholder text
        }),
        indicatorSeparator: (styles, state) => ({
            ...styles,
            margin: 0,
            backgroundColor: "none",
        }),
        indicatorsContainer: (styles, state) => ({
            ...styles,
            backgroundColor: bgColor,
            height: "100%",
            cursor: "pointer",
            borderRadius: "4px",
        }),
    };
};

const Select = React.forwardRef((props, ref) => {
    const { Dyn, ...props2 } = props;
    const [isOpened, setIsOpened] = useState(false);
    const [focusedOption, setFocusedOption] = useState(null);

    const handleKeyDown = useCallback((e) => {
        // Open on enter key press
        if (e.key === "Enter" && !isOpened) {
            setIsOpened(true);
        }
        if (e.key === "Escape" && isOpened) {
            e.stopPropagation();
        }

        // Update focused option on arrow key navigation
        if (isOpened && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
            // Let React Select handle the navigation, then update our state and scroll into view
            setTimeout(() => {
                const focusedElement = document.querySelector('.tde-select-option.tde-focused');
                if (focusedElement && focusedElement.id) {
                    setFocusedOption(focusedElement.id);
                    // Find the scrollable menu list container
                    const menuList = focusedElement.closest('[role="listbox"]');
                    if (menuList) {
                        // Calculate if the focused element is visible within the menu list
                        const menuRect = menuList.getBoundingClientRect();
                        const elementRect = focusedElement.getBoundingClientRect();

                        // Scroll the menu list container to bring the focused element into view
                        if (elementRect.top < menuRect.top) {
                            // Element is above visible area, scroll up to show it at the top
                            menuList.scrollTop = focusedElement.offsetTop;
                        } else if (elementRect.bottom > menuRect.bottom) {
                            // Element is below visible area, scroll down to show it at the bottom
                            const scrollBottom = focusedElement.offsetTop + focusedElement.offsetHeight - menuList.clientHeight;
                            menuList.scrollTop = scrollBottom;
                        }
                    }
                }
            }, 0);
        }
    }, [isOpened]);

    const handleMenuOpen = useCallback(() => {
        setIsOpened(true);
        // Set initial focused option to the selected value
        if (props2.value) {
            setFocusedOption(`react-select-option-${props2.value.value || props2.value}`);
        }
    }, [props2.value]);

    const handleMenuClose = useCallback(() => {
        setIsOpened(false);
        setFocusedOption(null);
    }, []);

    // Default accessible MenuList component
    const defaultMenuList = useCallback(({ children, className, style, id, ...rest }) => {
        // Filter out react-select internal props that shouldn't be on DOM elements
        // Only keep valid DOM attributes
        const domProps = {
            ...(className && { className }),
            ...(id && { id }),
            role: 'listbox',
            style: {
                ...style,
                maxHeight: props2.menuListMaxHeight || '200px',
                overflowY: 'auto'
            }
        };

        return (
            <div {...domProps}>
                {children}
            </div>
        );
    }, [props2.menuListMaxHeight]);

    // Merge default components with any custom ones
    const components = {
        MenuList: defaultMenuList,
        ...props2.components
    };

    return (
        <ReactSelect
            {...props2}
            ref={ref}
            components={components}
            styles={customSelectInputStyles(Dyn.preferences.getTheme())}
            onKeyDown={handleKeyDown}
            menuIsOpen={isOpened}
            menuShouldScrollIntoView={false}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            aria-activedescendant={isOpened && focusedOption ? focusedOption : undefined}
        />
    );
});

export default Select;
